import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  PhoneInput,
  isValidIndianMobile,
  normalizeIndianMobile,
} from '@/components/ui/phone-input';
import { updateProfile } from '@shared/api/auth';
import { apiErrorMessage } from '@shared/api/client';
import { deleteAvatar, uploadAvatar } from '@shared/api/users';
import { useMe, ME_QUERY_KEY } from '@shared/hooks/use-me';
import { fullName } from '@/lib/format';

export function ProfilePage() {
  const navigate = useNavigate();
  const { data: me } = useMe();
  const qc = useQueryClient();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobile, setMobile] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!me) return;
    setFirstName(me.firstName ?? '');
    setLastName(me.lastName ?? '');
    setMobile(normalizeIndianMobile(me.mobile));
  }, [me]);

  const save = useMutation({
    mutationFn: () =>
      updateProfile({
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
        mobile: mobile.trim() || undefined,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ME_QUERY_KEY });
      navigate('/client/home');
    },
  });

  const avatar = useMutation({
    mutationFn: (file: File) => uploadAvatar(file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });

  const removeAvatar = useMutation({
    mutationFn: () => deleteAvatar(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ME_QUERY_KEY }),
  });

  const dirty =
    (me?.firstName ?? '') !== firstName ||
    (me?.lastName ?? '') !== lastName ||
    normalizeIndianMobile(me?.mobile) !== mobile;

  function reset() {
    if (!me) return;
    setFirstName(me.firstName ?? '');
    setLastName(me.lastName ?? '');
    setMobile(normalizeIndianMobile(me.mobile));
  }

  function handleCancel() {
    reset();
    navigate('/client/home');
  }

  return (
    <div className="w-full max-w-4xl px-4 py-6 sm:px-6 sm:py-8">
      {/* Back to Menu Button (Mobile Only) */}
      <button
        type="button"
        onClick={() => navigate('/client/menu')}
        className="md:hidden inline-flex items-center gap-1.5 mb-4 text-xs font-bold text-gray-700 hover:text-brand-800 bg-white border border-gray-200 hover:border-brand-300 px-3 py-1.5 rounded-xl shadow-xs transition-all active:scale-95 cursor-pointer select-none"
      >
        <svg
          className="w-4 h-4 text-brand-700"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
        </svg>
        <span>Back to Menu</span>
      </button>

      <nav className="text-xs text-gray-500 sm:text-sm">
        <Link to="/client/home" className="hover:underline">
          Home
        </Link>
        {' / '}
        <span className="text-gray-900">Profile</span>
      </nav>
      <h1 className="mt-4 text-xl font-semibold text-gray-900 sm:text-2xl">Profile</h1>
      <p className="mt-1 text-sm text-gray-600">View, edit your profile details.</p>

      <section className="mt-6 rounded-xl border border-gray-200 bg-white p-4 sm:p-6">
        <h2 className="text-sm font-semibold text-gray-900">Basic Info</h2>

        <div className="mt-4 flex items-center gap-4">
          <Avatar url={me?.avatarUrl} name={fullName(firstName, lastName)} />
          <div className="flex-1">
            <p className="text-xs text-gray-500">We support JPG, PNG, or WebP images under 5 MB.</p>
            <div className="mt-2 flex gap-2">
              <Button
                variant="outline"
                className="px-3 py-1.5 text-xs"
                onClick={() => fileRef.current?.click()}
                disabled={avatar.isPending}
              >
                {avatar.isPending ? 'Uploading…' : 'Upload photo'}
              </Button>
              {me?.avatarUrl ? (
                <Button
                  variant="ghost"
                  className="px-3 py-1.5 text-xs"
                  onClick={() => removeAvatar.mutate()}
                  disabled={removeAvatar.isPending}
                >
                  Remove
                </Button>
              ) : null}
            </div>
            {avatar.isError ? (
              <p className="mt-1 text-xs text-red-600">
                {apiErrorMessage(avatar.error, (avatar.error as Error)?.message)}
              </p>
            ) : null}
            <input
              ref={fileRef}
              type="file"
              hidden
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) avatar.mutate(f);
                e.target.value = '';
              }}
            />
          </div>
        </div>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <Field label="First name">
            <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          </Field>
          <Field label="Last name">
            <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
          </Field>
          <Field label="Email" hint="Email changes require re-verification.">
            <Input value={me?.email ?? ''} readOnly disabled />
          </Field>
          <Field
            label="Phone number"
            hint={
              mobile && !isValidIndianMobile(mobile)
                ? 'Enter a 10-digit Indian mobile number.'
                : 'Indian numbers only.'
            }
          >
            <PhoneInput value={mobile} onChange={setMobile} />
          </Field>
        </div>

        {save.isError ? (
          <p className="mt-4 text-sm text-red-600">{apiErrorMessage(save.error)}</p>
        ) : null}
        {save.isSuccess && !dirty ? (
          <p className="mt-4 text-sm text-brand-700">Profile saved.</p>
        ) : null}

        <div className="mt-6 flex justify-end gap-3">
          <Button variant="ghost" onClick={handleCancel} disabled={save.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => save.mutate()}
            disabled={!dirty || save.isPending || (!!mobile && !isValidIndianMobile(mobile))}
          >
            {save.isPending ? 'Saving…' : 'Save Profile'}
          </Button>
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-gray-700">{label}</div>
      {children}
      {hint ? <div className="mt-1 text-xs text-gray-500">{hint}</div> : null}
    </label>
  );
}

function Avatar({ url, name }: { url: string | null | undefined; name: string }) {
  if (url) return <img src={url} alt="" className="h-16 w-16 rounded-full object-cover" />;
  const initials = name
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <span className="flex h-16 w-16 items-center justify-center rounded-full bg-brand-100 text-lg font-semibold text-brand-700">
      {initials || 'U'}
    </span>
  );
}
